import { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createHiringRequestScorecard,
  deleteHiringRequestScorecard,
  editHiringRequestScorecard,
} from './scorecardQueries';
import {
  CreateScorecardMutationVariables,
  DeleteScorecardMutationVariables,
  EditScorecardMutationVariables,
} from '../types/scorecardTypes';
import { HiringRequestListAPIData } from '../types';
import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import { HiringRequestScorecard } from '../types/feedbackDetailTypes';

export function useCreateHiringRequestScorecard(
  districtId: number,
  options: Omit<
    UseMutationOptions<
      HiringRequestScorecard,
      AxiosError,
      CreateScorecardMutationVariables,
      unknown
    >,
    'mutationFn'
  > = {}
): UseMutationResult<
  HiringRequestScorecard,
  AxiosError,
  CreateScorecardMutationVariables,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hiringRequestId, payload }) =>
      createHiringRequestScorecard(hiringRequestId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);
      const currentQueryData: HiringRequestScorecard[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey
      );

      const updatedData = currentQueryData.concat(data);

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey,
        updatedData
      );

      const currentHiringRequestListData: HiringRequestListAPIData = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey
      );
      const updatedResults = currentHiringRequestListData.results?.map((hiringRequest) => {
        if (
          hiringRequest.id === variables.hiringRequestId &&
          !hiringRequest.has_scorecards &&
          data.scorecard_complete
        ) {
          return {
            ...hiringRequest,
            has_scorecards: true,
          };
        }
        return hiringRequest;
      });

      queryClient.setQueryData(hiringRequestQueries.hiringRequests.list(districtId).queryKey, {
        ...currentHiringRequestListData,
        results: updatedResults,
      });
    },
  });
}

export function useEditHiringRequestScorecard(
  districtId: number,
  options: Omit<
    UseMutationOptions<HiringRequestScorecard, AxiosError, EditScorecardMutationVariables, unknown>,
    'mutationFn'
  > = {}
): UseMutationResult<HiringRequestScorecard, AxiosError, EditScorecardMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hiringRequestId, scorecardId, payload }) =>
      editHiringRequestScorecard(hiringRequestId, scorecardId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      const currentQueryData: HiringRequestScorecard[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey
      );

      const updatedData = currentQueryData.map((scorecard) =>
        scorecard.id === data.id ? data : scorecard
      );

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey,
        updatedData
      );

      const currentHiringRequestListData: HiringRequestListAPIData = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey
      );

      const updatedResults = currentHiringRequestListData.results?.map((hiringRequest) => {
        if (hiringRequest.id === variables.hiringRequestId && data.scorecard_complete) {
          return {
            ...hiringRequest,
            has_scorecards: true,
          };
        }
        return hiringRequest;
      });
      queryClient.setQueryData(hiringRequestQueries.hiringRequests.list(districtId).queryKey, {
        ...currentHiringRequestListData,
        results: updatedResults,
      });
    },
  });
}

export function useDeleteHiringRequestScorecard(
  districtId: number,
  options: Omit<
    UseMutationOptions<HiringRequestScorecard, unknown, DeleteScorecardMutationVariables, unknown>,
    'mutationFn'
  > = {}
): UseMutationResult<HiringRequestScorecard, unknown, DeleteScorecardMutationVariables, unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scorecardId }) => {
      const response = await deleteHiringRequestScorecard(id, scorecardId);
      return response;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const currentQueryData: HiringRequestScorecard[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.id).queryKey
      );

      const updatedData = currentQueryData.filter((datum) => datum.id !== variables.scorecardId);

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.id).queryKey,
        updatedData
      );

      const currentHiringRequestListData: HiringRequestListAPIData = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey
      );

      const hasNoScorecardsOrAllIncomplete =
        updatedData.length === 0 || updatedData.every((scorecard) => !scorecard.scorecard_complete);

      if (hasNoScorecardsOrAllIncomplete) {
        const updatedResults = currentHiringRequestListData.results?.map((hiringRequest) => {
          const isHiringRequestToUpdate = hiringRequest.id === variables.id;

          if (isHiringRequestToUpdate && hasNoScorecardsOrAllIncomplete) {
            return {
              ...hiringRequest,
              has_scorecards: false,
            };
          }
          return hiringRequest;
        });

        queryClient.setQueryData(hiringRequestQueries.hiringRequests.list(districtId).queryKey, {
          ...currentHiringRequestListData,
          results: updatedResults,
        });
      }
      options.onSuccess?.(data, variables, context);
    },
  });
}
